/**
 * Facebook login using the official Facebook JS SDK.
 *
 * The SDK is pre-loaded on component mount (initFacebook) so that
 * FB.login() can be called synchronously from the user click context
 * (required to avoid popup blockers).
 *
 * Unlike google.ts (popup + URL polling), we use the SDK because:
 * - Facebook deprecated implicit grant (response_type=token) for newer apps
 * - SDK handles popup/redirect/CSRF automatically
 * - SDK is the officially supported approach for web login
 */

const FB_SDK_URL = "https://connect.facebook.net/en_US/sdk.js";
const FB_GRAPH_URL = "https://graph.facebook.com/v19.0";

let sdkLoadPromise: Promise<void> | null = null;

/**
 * Pre-load the Facebook JS SDK. Call this on component mount,
 * NOT on button click — FB.login() must run synchronously from
 * the click handler's call stack to avoid popup blockers.
 */
export function initFacebook(appId: string): Promise<void> {
    if (sdkLoadPromise) return sdkLoadPromise;

    const win = window as any;

    // SDK already loaded (e.g. by share dialog in blogs)
    if (win.FB) {
        sdkLoadPromise = Promise.resolve();
        return sdkLoadPromise;
    }

    sdkLoadPromise = new Promise<void>((resolve, reject) => {
        // Preserve existing fbAsyncInit if set by another feature (blogs share)
        const existingInit = win.fbAsyncInit;

        win.fbAsyncInit = () => {
            win.FB.init({
                appId,
                cookie: false,
                xfbml: false,
                version: "v19.0",
            });
            resolve();

            if (typeof existingInit === "function") {
                existingInit();
            }
        };

        // Don't add script if already in DOM (another feature may have added it)
        if (!document.getElementById("facebook-jssdk")) {
            const script = document.createElement("script");
            script.id = "facebook-jssdk";
            script.src = FB_SDK_URL;
            script.async = true;
            script.onerror = () => {
                sdkLoadPromise = null; // allow retry on next mount
                reject(new Error("Failed to load Facebook SDK"));
            };
            document.head.appendChild(script);
        }
    });

    return sdkLoadPromise;
}

/**
 * Opens Facebook login dialog and returns the access_token.
 * The SDK must be pre-loaded via initFacebook() before calling this.
 */
export function signInWithFacebook(): Promise<string> {
    const FB = (window as any).FB;
    if (!FB) {
        return Promise.reject(new Error("Facebook SDK not loaded. Please try again."));
    }

    return new Promise((resolve, reject) => {
        FB.login(
            (response: any) => {
                if (response.authResponse?.accessToken) {
                    resolve(response.authResponse.accessToken);
                } else {
                    reject(new Error("Facebook sign-in was cancelled"));
                }
            },
            { scope: "email,public_profile", return_scopes: true },
        );
    });
}

/**
 * Fetch basic user info from Facebook Graph API.
 * Uses Bearer token in Authorization header (not query param)
 * to avoid leaking the access_token in logs/browser history.
 */
export async function getFacebookUserInfo(
    accessToken: string,
): Promise<{ email?: string; name?: string }> {
    const response = await fetch(`${FB_GRAPH_URL}/me?fields=email,name`, {
        headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!response.ok) {
        throw new Error("Failed to fetch Facebook user info");
    }
    const data = await response.json();
    return {
        email: data.email || undefined,
        name: data.name || undefined,
    };
}
